import { useCallback, useEffect, useMemo, useState } from "react";
import { memberService, ledgerService } from "@/services";
import type { LedgerEntry, LedgerEntryType, Member } from "@/services/types";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { EmptyState } from "@/components/base/EmptyState";
import { ErrorState } from "@/components/base/ErrorState";
import { DataTable, type DataTableColumn } from "@/components/feature/DataTable";
import { DateRangePicker, type DateRange } from "@/components/base/DateRangePicker";
import { Select } from "@/components/base/Select";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDateTime } from "@/utils/format";
import { LEDGER_TYPE_META } from "../components/labels";

const TYPE_OPTIONS = Object.entries(LEDGER_TYPE_META).map(([value, meta]) => ({ value, label: meta.label }));

export default function RawLedgerPage() {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [memberId, setMemberId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [entries, setEntries] = useState<LedgerEntry[] | null>(null);
  const [groupFilter, setGroupFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [range, setRange] = useState<DateRange>({ start: null, end: null });

  const load = useCallback(async () => {
    setError(null);
    try {
      const list = await memberService.listMembers();
      setMembers(list);
      setMemberId((prev) => prev || list[0]?.id || "");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load members.");
      setMembers([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const fetchEntries = useCallback(async () => {
    if (!memberId) return;
    setError(null);
    try {
      setEntries(await ledgerService.getMemberLedger(memberId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the ledger.");
      setEntries([]);
    }
  }, [memberId]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const groups = useMemo(() => {
    const map = new Map<string, string>();
    for (const e of entries ?? []) map.set(e.groupId, e.groupName);
    return Array.from(map.entries()).map(([id, name]) => ({ value: id, label: name }));
  }, [entries]);

  const filtered = useMemo(() => {
    let rows = entries ?? [];
    if (groupFilter) rows = rows.filter((e) => e.groupId === groupFilter);
    if (typeFilter) rows = rows.filter((e) => e.type === typeFilter);
    if (range.start) rows = rows.filter((e) => new Date(e.date) >= new Date(range.start as string));
    if (range.end) {
      const end = new Date(range.end as string);
      end.setHours(23, 59, 59, 999);
      rows = rows.filter((e) => new Date(e.date) <= end);
    }
    return rows;
  }, [entries, groupFilter, typeFilter, range]);

  const activeFilters = (groupFilter ? 1 : 0) + (typeFilter ? 1 : 0) + (range.start ? 1 : 0);
  const clearFilters = () => {
    setGroupFilter("");
    setTypeFilter("");
    setRange({ start: null, end: null });
  };

  const columns: DataTableColumn<LedgerEntry>[] = [
    {
      id: "date",
      header: "Date",
      accessor: (e) => <span className="tnum whitespace-nowrap text-xs">{formatDateTime(e.date)}</span>,
      sortValue: (e) => e.date,
      sortable: true,
    },
    {
      id: "group",
      header: "Group",
      accessor: (e) => (
        <span className="whitespace-nowrap text-sm">
          {e.groupName}
          {e.cycle ? <span className="tnum ml-1 text-xs text-foreground-400">C{e.cycle}</span> : null}
        </span>
      ),
      sortValue: (e) => e.groupName,
      sortable: true,
    },
    {
      id: "type",
      header: "Type",
      accessor: (e) => (
        <div className="flex items-center gap-1.5">
          <Badge color={LEDGER_TYPE_META[e.type].badge} dot>{LEDGER_TYPE_META[e.type].label}</Badge>
          {e.reversed ? <Badge color="danger">Reversed</Badge> : null}
        </div>
      ),
      sortValue: (e) => e.type,
      sortable: true,
    },
    {
      id: "narration",
      header: "Narration",
      accessor: (e) => <span className="max-w-[260px] text-sm text-foreground-700">{e.narration}</span>,
      sortValue: (e) => e.narration,
      sortable: true,
    },
    {
      id: "debit",
      header: "Debit",
      align: "right",
      accessor: (e) => (e.debit > 0 ? <CurrencyDisplay amount={e.debit} hideSymbol /> : <span className="text-foreground-300">—</span>),
      sortValue: (e) => e.debit,
      sortable: true,
      footerValue: (rows) => <CurrencyDisplay amount={rows.reduce((s, r) => s + r.debit, 0)} hideSymbol />,
    },
    {
      id: "credit",
      header: "Credit",
      align: "right",
      accessor: (e) => (e.credit > 0 ? <CurrencyDisplay amount={e.credit} hideSymbol tone="success" /> : <span className="text-foreground-300">—</span>),
      sortValue: (e) => e.credit,
      sortable: true,
      footerValue: (rows) => <CurrencyDisplay amount={rows.reduce((s, r) => s + r.credit, 0)} hideSymbol />,
    },
    {
      id: "balance",
      header: "Running balance",
      align: "right",
      accessor: (e) => (
        <CurrencyDisplay
          amount={e.runningBalance}
          hideSymbol
          tone={e.runningBalance > 0 ? "danger" : e.runningBalance < 0 ? "success" : "default"}
          className="font-medium"
        />
      ),
      sortValue: (e) => e.runningBalance,
      sortable: true,
      footerValue: (rows) => {
        const last = [...rows].filter((r) => !r.reversed);
        const balance = last.length ? last[last.length - 1].runningBalance : 0;
        return <CurrencyDisplay amount={balance} hideSymbol className="font-semibold" tone={balance > 0 ? "danger" : balance < 0 ? "success" : "default"} />;
      },
    },
    {
      id: "ref",
      header: "Reference",
      accessor: (e) => <span className="tnum whitespace-nowrap text-xs text-foreground-500">{e.reference}</span>,
      sortValue: (e) => e.reference,
      sortable: true,
    },
  ];

  return (
    <div>
      <PageHeader
        breadcrumb={
          <>
            <BreadcrumbItem label="Home" to="/" />
            <BreadcrumbItem label="Reports" to="/reports" />
            <BreadcrumbItem label="Raw ledger" isLast />
          </>
        }
        title="Raw ledger"
        subtitle="The full chronological audit trail for a member — used to investigate any discrepancy."
      />

      <Card>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-foreground-500">Member</span>
            <Select
              value={memberId}
              onChange={setMemberId}
              options={(members ?? []).map((m) => ({ value: m.id, label: `${m.fullName} — ${m.memberCode}` }))}
              placeholder="Select member"
              className="w-72"
            />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-foreground-500">Group</span>
            <Select value={groupFilter} onChange={setGroupFilter} options={groups} placeholder="All groups" className="w-56" />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-foreground-500">Entry type</span>
            <Select value={typeFilter} onChange={setTypeFilter} options={TYPE_OPTIONS} placeholder="All types" className="w-52" />
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium text-foreground-500">Date range</span>
            <DateRangePicker value={range} onChange={setRange} />
          </div>
          {activeFilters > 0 ? (
            <button type="button" onClick={clearFilters} className="cursor-pointer text-xs font-medium text-primary-600 hover:underline">
              Clear filters ({activeFilters})
            </button>
          ) : null}
        </div>
      </Card>

      {error ? <div className="mt-3"><ErrorState message={error} onRetry={fetchEntries} /></div> : null}

      <div className="mt-3">
        <DataTable<LedgerEntry>
          columns={columns}
          rows={entries}
          rowKey={(e) => e.id}
          loading={false}
          error={error}
          onRetry={fetchEntries}
          emptyMessage="No ledger entries"
          emptyDescription="No entries match these filters for this member."
          showTotals
          totalLabel="Total"
          showDensityToggle={false}
          showColumnVisibility
          defaultPageSize={14}
          initialSort={{ id: "date", dir: "desc" }}
        />
        {!error && entries && entries.length === 0 ? (
          <Card className="mt-3">
            <EmptyState icon="ri-list-unordered" title="No ledger entries" description="This member has no financial activity yet." />
          </Card>
        ) : null}
      </div>
    </div>
  );
}